import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
// Common Components
import AppBreadCrumb from '../../common/AppBreadCrumb'; // Correct path
import AppToaster from '../../AppToaster'; // Correct path
// API & Utils
import { createGrn, fetchGrn, updateGrn, fetchGrnPrepopulationData } from '../../../api/GoodsReceivedNotesAPI'; // Correct API
// ** Import APIs needed for manual item addition **
import { getProducts } from '../../../api/ProductsAPI'; // Correct path
import { getUoms } from '../../../api/UomsAPI'; // Correct path
import { handleApiError } from '../../../api/risapi'; // Correct path
// Hooks & Helpers
import { useNavigate, useParams } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions'; // Correct path
// Date Picker
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

// Props include sourceType and sourceId for pre-population
const GrnForm = ({ formType, currentGrnId, sourceType = null, sourceId = null }) => {
    const navigate = useNavigate();
    const [grn, setGrn] = useState({
        grn_date: new Date(),
        sourceable_type: sourceType, // From props/URL
        sourceable_id: sourceId ? parseInt(sourceId, 10) : null, // From props/URL
        source_display: null, // To show source info (e.g., PI number)
        items: [], // Array for GRN items
    });
    const [errors, setErrors] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    // Separate loading states
    const [loadingDropdowns, setLoadingDropdowns] = useState(true); // For manual add dropdowns
    const [loadingGrnData, setLoadingGrnData] = useState(false); // For fetching existing GRN
    const [loadingPrepop, setLoadingPrepop] = useState(false); // For fetching pre-population data
    const [saving, setSaving] = useState(false);

    // Dropdown options
    const [productOptions, setProductOptions] = useState([]);
    const [uomOptions, setUomOptions] = useState([]);

    // --- Fetch Dropdown Data (Needed for Manual Add) ---
    useEffect(() => {
        let isMounted = true;
        setLoadingDropdowns(true); // Start loading dropdowns
        const fetchDropdowns = async () => {
            // Only fetch if needed (manual create or edit mode allowing manual add - currently edit doesn't allow adding)
            // if (formType === 'edit' && sourceType) {
            //      setLoadingDropdowns(false); // Don't need dropdowns if purely editing pre-populated
            //      return;
            // }
            try {
                // Fetch Products and UOMs
                const [productResponse, uomResponse] = await Promise.all([
                    new Promise((resolve) => getProducts({ paginate: false }, resolve)),
                    new Promise((resolve) => getUoms({ paginate: false }, resolve)),
                ]);

                if (!isMounted) return;

                // Process Products (Store full data for UOM filtering)
                if (productResponse.products && Array.isArray(productResponse.products)) {
                     setProductOptions(productResponse.products.map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}`, productData: p })));
                 } else { throw new Error('Invalid product data'); }

                // Process UOMs
                if (uomResponse.uoms && Array.isArray(uomResponse.uoms)) {
                     setUomOptions(uomResponse.uoms.map(u => ({ value: u.id, label: `${u.name} (${u.short_name})` })));
                 } else { throw new Error('Invalid UOM data'); }

            } catch (error) {
                 if (!isMounted) return;
                console.error("Error fetching dropdown data:", error);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Product/UOM data for manual entry.' });
                setShowToaster(true);
                setProductOptions([]); setUomOptions([]);
            } finally {
                 if (isMounted) setLoadingDropdowns(false); // Stop dropdown loading
            }
        };
        fetchDropdowns();
        return () => { isMounted = false; };
    }, []); // Run only once

    // --- Fetch Initial Data (Pre-population or Edit) ---
    useEffect(() => {
        let isMounted = true;
        setErrors({});
        setGrn(prev => ({ // Reset GRN state, keeping source info if present
            ...prev,
             grn_date: new Date(),
             source_display: prev.sourceable_type ? prev.source_display : 'Manual Entry',
             items: []
        }));


        if (formType === 'edit' && currentGrnId) {
            setLoadingGrnData(true); setLoadingPrepop(false);
            fetchGrn(currentGrnId, (response) => {
                 if (!isMounted) return;
                 setLoadingGrnData(false);
                 if (response.grn) {
                     const fetched = response.grn;
                     let grnDate = new Date();
                     if (fetched.grn_date) { try { grnDate = parseISO(fetched.grn_date + 'T00:00:00'); if(isNaN(grnDate)) grnDate = new Date(); } catch(e){ grnDate = new Date(); } }
                     const formattedItems = (Array.isArray(fetched.items) ? fetched.items : []).map(item => ({ id: item.id, product_id: item.product_id, product_name: item.product?.name || 'Unknown', received_uom_id: item.received_uom_id, uom_name: item.uom?.short_name || 'N/A', received_quantity: item.received_quantity || '', conversion_factor: item.conversion_factor || 1, landed_cost: item.landed_cost || '' }));
                     let sourceDisplay = 'Manual Entry';
                     if (fetched.sourceable) { const type = fetched.sourceable_type.split('\\').pop(); const number = fetched.sourceable.invoice_number || fetched.sourceable.challan_number || `#${fetched.sourceable_id}`; const partyName = fetched.sourceable.party?.name || ''; sourceDisplay = `${type} #${number} ${partyName ? `(${partyName})` : ''}`; }
                     setGrn({ id: fetched.id, grn_date: grnDate, sourceable_type: fetched.sourceable_type, sourceable_id: fetched.sourceable_id, source_display: sourceDisplay, items: formattedItems });
                 } else { handleApiError(response, () => {}); setToaster({ type: 'danger', heading: 'Error', message: 'Could not load GRN data.' }); setShowToaster(true); }
            });
        } else if (formType === 'create' && sourceType && sourceId) {
             setLoadingPrepop(true); setLoadingGrnData(false);
             fetchGrnPrepopulationData(sourceType, sourceId, (response) => {
                  if (!isMounted) return;
                  setLoadingPrepop(false);
                  if (response.prepopulation_data) {
                       const prepop = response.prepopulation_data;
                       const formattedItems = (Array.isArray(prepop.items) ? prepop.items : []).map(item => ({ id: null, product_id: item.product_id, product_name: item.product_name || 'Unknown', received_uom_id: item.received_uom_id, uom_name: item.uom_name || 'N/A', received_quantity: item.received_quantity || '', conversion_factor: item.conversion_factor || 1, landed_cost: item.landed_cost || '' }));
                       let sourceDisplay = 'Manual Entry'; if (prepop.source) { sourceDisplay = `${prepop.source.type} #${prepop.source.number || prepop.source.id} (${prepop.source.party_name || ''})`; }
                       setGrn({ grn_date: new Date(), sourceable_type: sourceType, sourceable_id: parseInt(sourceId, 10), source_display: sourceDisplay, items: formattedItems });
                  } else { handleApiError(response, () => {}); setToaster({ type: 'danger', heading: 'Error', message: response.error?.response?.data?.message || 'Could not load pre-population data.' }); setShowToaster(true); setGrn(prev => ({ ...prev, sourceable_type: null, sourceable_id: null, source_display: 'Error Loading Source', items: []})); }
             });
        } else { // Manual create mode
             setLoadingGrnData(false); setLoadingPrepop(false);
             setGrn({ grn_date: new Date(), sourceable_type: null, sourceable_id: null, source_display: 'Manual Entry', items: [] });
             // Dropdowns are fetched by the separate useEffect
        }
        return () => { isMounted = false; };
    }, [formType, currentGrnId, sourceType, sourceId, navigate]);


    // --- Item Handlers ---
    const addItem = () => {
        setGrn(prev => ({
            ...prev,
            items: [
                ...(Array.isArray(prev.items) ? prev.items : []),
                { id: null, product_id: null, product_name: '', received_uom_id: null, uom_name: '', received_quantity: '', conversion_factor: 1, landed_cost: '' }
            ],
        }));
    };

    const handleItemChange = (index, fieldName, value) => {
        setGrn(prev => {
            const newItems = [...(prev.items || [])];
            if (!newItems[index]) return prev;

            let updatedValue = value;
            let dependentUpdates = {};
            const currentItem = { ...newItems[index] };

            // --- Logic specific to manual item changes ---
            if (fieldName === 'product_id') {
                 updatedValue = value ? parseInt(value, 10) : null;
                 dependentUpdates.received_uom_id = null; // Reset UOM
                 dependentUpdates.conversion_factor = 1; // Reset factor
                 dependentUpdates.product_name = productOptions.find(p => p.value === updatedValue)?.label || ''; // Update name
                 // Clear UOM name too
                 dependentUpdates.uom_name = '';
            } else if (fieldName === 'received_uom_id') {
                 updatedValue = value ? parseInt(value, 10) : null;
                 dependentUpdates.uom_name = uomOptions.find(u => u.value === updatedValue)?.label || '';
                 // Lookup and set conversion factor
                 const productData = productOptions.find(p => p.value === currentItem.product_id)?.productData;
                 if (productData && updatedValue) {
                     if (productData.base_uom_id === updatedValue) {
                          dependentUpdates.conversion_factor = 1;
                     } else {
                          const productUnit = productData.product_units?.find(pu => pu.uom_id === updatedValue);
                          dependentUpdates.conversion_factor = productUnit?.conversion_factor || 1;
                     }
                 } else { dependentUpdates.conversion_factor = 1; }
            }
             // --- End of manual item change logic ---
             else if (fieldName === 'received_quantity' || fieldName === 'landed_cost') {
                 updatedValue = value === '' ? '' : value;
                 if (updatedValue !== '') { const numValue = parseFloat(updatedValue); if (isNaN(numValue) || numValue < 0) return prev; }
            }

            newItems[index] = { ...currentItem, [fieldName]: updatedValue, ...dependentUpdates };
            return { ...prev, items: newItems };
        });
        setErrors(prev => { const newErrors = {...prev}; if(newErrors.items?.[index]?.[fieldName]) delete newErrors.items[index][fieldName]; return newErrors; });
    };

    const removeItem = (index) => {
        setGrn(prev => ({ ...prev, items: (Array.isArray(prev.items) ? prev.items : []).filter((_, i) => i !== index) }));
        setErrors(prevErr => { const newItemsErrors = { ...(prevErr.items || {}) }; delete newItemsErrors[index]; return { ...prevErr, items: newItemsErrors }; });
    };

    // Helper to get valid UOMs for a specific product ID
     const getFilteredUomOptionsForItem = (productId) => {
         if (!productId || productOptions.length === 0 || uomOptions.length === 0) return [];
         const product = productOptions.find(p => p.value === productId)?.productData;
         if (!product) return [];
         const validUomIds = new Set();
         if (product.base_uom_id) validUomIds.add(product.base_uom_id);
         if (Array.isArray(product.product_units)) { product.product_units.forEach(unit => { if (unit.uom_id) validUomIds.add(unit.uom_id); }); }
         return uomOptions.filter(uom => validUomIds.has(uom.value));
     };


    // --- Save Handler ---
    const saveForm = (exitForm) => {
        setErrors({}); setSaving(true);
        const apiCall = formType === 'create' ? createGrn : updateGrn;
        let formattedDate = null;
        if (grn.grn_date instanceof Date && !isNaN(grn.grn_date)) { formattedDate = format(grn.grn_date, 'yyyy-MM-dd'); }
        else { setErrors({ grn_date: 'Invalid GRN date.' }); setSaving(false); return; }

        const cleanedItems = (Array.isArray(grn.items) ? grn.items : [])
             .filter(item => item.product_id && item.received_uom_id && item.received_quantity !== '' && item.landed_cost !== '' && item.conversion_factor)
             .map(item => ({
                 id: item.id, product_id: item.product_id, received_uom_id: item.received_uom_id,
                 received_quantity: item.received_quantity, conversion_factor: item.conversion_factor, landed_cost: item.landed_cost,
             }));

        if (cleanedItems.length === 0) { setErrors({ items: 'At least one valid item is required.'}); setSaving(false); return; }
        if (formType === 'create' && cleanedItems.length !== (grn.items || []).length) {
             setToaster({ type: 'warning', heading: 'Missing Info', message: 'Some items had missing details and were not saved.' }); setShowToaster(true);
        }

        const dataToSend = {
             id: grn.id, grn_date: formattedDate,
             sourceable_type: grn.sourceable_type, sourceable_id: grn.sourceable_id,
             items: cleanedItems,
        };
        if (formType === 'create') { delete dataToSend.id; }
        if (!dataToSend.sourceable_type || !dataToSend.sourceable_id) { dataToSend.sourceable_type = null; dataToSend.sourceable_id = null; }

        apiCall(dataToSend, (response) => {
            setSaving(false);
            if (response.grn) {
                 const message = `GRN ${formType === 'create' ? 'created' : 'updated'} successfully! GRN No: ${response.grn.grn_number || '#' + response.grn.id}`;
                 navigate('/franchisee_user/inventory/grn', { state: { message } });
            } else {
                 handleApiError(response, setErrors);
                 setToaster({ type: 'danger', heading: 'Error', message: `Could not ${formType} GRN.` }); setShowToaster(true);
            }
        });
    };

    // --- Reset Handler ---
    const handleReset = () => {
         if (formType === 'create') {
             if (sourceType && sourceId) {
                  setLoadingPrepop(true);
                  fetchGrnPrepopulationData(sourceType, sourceId, (response) => { setLoadingPrepop(false); /* ... update state ... */ });
             } else {
                 setGrn({ grn_date: new Date(), sourceable_type: null, sourceable_id: null, source_display: 'Manual Entry', items: [] }); setErrors({});
             }
         } else if (currentGrnId){
              setLoadingGrnData(true);
              fetchGrn(currentGrnId, (response) => { setLoadingGrnData(false); /* ... update state ... */ });
         }
    };


    const getTitle = () => formType === 'create' ? 'Create Goods Received Note' : 'Edit Goods Received Note';
    const isLoading = loadingGrnData || loadingPrepop || loadingDropdowns; // Combine all loading states

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title={getTitle()}>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li><a className="f-s-14 f-w-500" href="/franchisee_user/inventory/grn">Goods Received Notes</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                    </AppBreadCrumb>

                    {isLoading && <div className="loader_box"><div className="loader_14"></div></div>}

                    {!isLoading && (
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                         <h5>{getTitle()} {formType === 'edit' ? `#${grn.grn_number || grn.id}` : ''}</h5>
                                         {grn.source_display && <small className="text-muted d-block">Source: {grn.source_display}</small>}
                                    </div>
                                    <div className="card-body">
                                        <form className="app-form needs-validation" noValidate onSubmit={(e) => e.preventDefault()}>
                                             {/* --- Header Section (as before) --- */}
                                             <div className="row border-bottom pb-3 mb-3">
                                                 <div className="col-md-4 mb-3">
                                                      <label className="form-label">GRN Date *</label>
                                                      <DatePicker selected={grn.grn_date} onChange={date => setGrn(prev => ({ ...prev, grn_date: date }))} dateFormat="yyyy-MM-dd" className="form-control" wrapperClassName="d-block" />
                                                      <span className="text-danger">{errors?.grn_date}</span>
                                                 </div>
                                                 <div className="col-md-8 mb-3">
                                                      <label className="form-label">Source Document</label>
                                                      <input type="text" className="form-control" value={grn.source_display || 'Manual Entry'} readOnly disabled />
                                                 </div>
                                             </div>

                                             {/* --- Items Section --- */}
                                             <h6 className="mb-3">Received Items</h6>
                                             <span className="text-danger d-block mb-2">{errors?.items && typeof errors.items === 'string' ? errors.items : ''}</span>
                                             <div className="table-responsive mb-3">
                                                  <table className="table table-bordered table-sm">
                                                       <thead className='table-light'>
                                                           <tr>
                                                               <th style={{width: '30%'}}>Product *</th>
                                                               <th style={{width: '15%'}}>Received UOM *</th>
                                                               <th style={{width: '15%'}} className='text-end'>Received Qty *</th>
                                                               <th style={{width: '10%'}} className='text-end'>Conv. Factor</th>
                                                               <th style={{width: '20%'}} className='text-end'>Landed Cost (per Base Unit) *</th>
                                                               <th style={{width: '5%'}}></th> {/* Action Column */}
                                                           </tr>
                                                       </thead>
                                                       <tbody>
                                                            {Array.isArray(grn.items) && grn.items.map((item, index) => (
                                                                <tr key={item.id || `item-${index}`}>
                                                                     {/* Product - Select if manual, Text if pre-populated/edit */}
                                                                     <td>
                                                                          {formType === 'create' && !sourceType ? (
                                                                               <Select options={productOptions} value={productOptions.find(o => o.value === item.product_id)} onChange={(sel) => handleItemChange(index, 'product_id', sel?.value)} placeholder="Select Product" isClearable isDisabled={loadingDropdowns} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}/>
                                                                          ) : ( <span className='align-middle d-block pt-2'>{item.product_name || 'N/A'}</span> )}
                                                                          <span className="text-danger d-block mt-1">{errors?.items?.[index]?.product_id}</span>
                                                                     </td>
                                                                     {/* UOM - Select if manual, Text if pre-populated/edit */}
                                                                     <td>
                                                                          {formType === 'create' && !sourceType ? (
                                                                               <Select options={getFilteredUomOptionsForItem(item.product_id)} value={uomOptions.find(o => o.value === item.received_uom_id)} onChange={(sel) => handleItemChange(index, 'received_uom_id', sel?.value)} placeholder="Select UOM" isClearable isDisabled={!item.product_id || loadingDropdowns} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}/>
                                                                          ) : ( <span className='align-middle d-block pt-2'>{item.uom_name || 'N/A'}</span> )}
                                                                          <span className="text-danger d-block mt-1">{errors?.items?.[index]?.received_uom_id}</span>
                                                                     </td>
                                                                     {/* Received Qty (Editable) */}
                                                                     <td>
                                                                          <input type="number" step="0.01" min="0" className="form-control text-end" value={item.received_quantity || ''} onChange={(e) => handleItemChange(index, 'received_quantity', e.target.value)} required />
                                                                          <span className="text-danger d-block mt-1">{errors?.items?.[index]?.received_quantity}</span>
                                                                     </td>
                                                                      {/* Conversion Factor (Readonly/Auto-filled) */}
                                                                      <td className='text-end align-middle'>{item.conversion_factor || '1.00'}</td>
                                                                     {/* Landed Cost (Editable) */}
                                                                      <td>
                                                                          <input type="number" step="0.01" min="0" className="form-control text-end" value={item.landed_cost || ''} onChange={(e) => handleItemChange(index, 'landed_cost', e.target.value)} required />
                                                                           <span className="text-danger d-block mt-1">{errors?.items?.[index]?.landed_cost}</span>
                                                                     </td>
                                                                      {/* Action Cell */}
                                                                      <td className='text-center align-middle'>
                                                                          {(formType === 'edit' || (formType === 'create' && !sourceType)) && (
                                                                              <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeItem(index)} title="Remove Item">&times;</button>
                                                                          )}
                                                                      </td>
                                                                </tr>
                                                            ))}
                                                             {/* No items row */}
                                                             {(!Array.isArray(grn.items) || grn.items.length === 0) && (
                                                                 <tr><td colSpan="6" className="text-center text-muted">No items found/added for this GRN.</td></tr>
                                                             )}
                                                       </tbody>
                                                       {/* Footer for adding items manually */}
                                                         {(formType === 'create' && !sourceType) && ( // Show only in manual create mode
                                                             <tfoot><tr><td colSpan="6"><button type="button" className="btn btn-sm btn-outline-success" onClick={addItem} disabled={loadingDropdowns}>+ Add Item Manually</button></td></tr></tfoot>
                                                         )}
                                                  </table>
                                             </div>

                                            {/* --- Action Buttons --- */}
                                            <div className="col-12 mt-4">
                                                <div className="text-end border-top pt-3">
                                                    {formType === 'create' ? (
                                                        <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving || isLoading}>Save GRN</button> // Disable if still loading dropdowns
                                                    ) : (
                                                        <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving || isLoading}>Update GRN</button>
                                                    )}
                                                     <button className="btn btn-secondary me-2 btn-sm" type="button" onClick={handleReset} disabled={saving || isLoading}>Reset</button>
                                                     <button className="btn btn-light-reddit btn-sm" type="button" onClick={() => navigate('/franchisee_user/inventory/grn')} disabled={saving}>Go Back</button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default GrnForm;